import "./styles.scss";

import { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  createStudentLevelGrade,
  cycleByLevelAndLanguage,
  updateStudentLevelGrade,
  getNextContentsOfStudent,
  getCurrentCycleAndLevelByStudentId,
} from "../../../store/middleware";
import { handleSelectColor } from "../../../utils/utils";

function ModalSelectLevelStudent({ showModal, setShowModal }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { report, cycles, currentCycleAndLevelStudent } = useSelector((state) => state.methodologyReducer);

  const maxLevelId = report[0]?.lingoLanguage.maxLevelId;
  const customLang = report[0]?.lingoLanguage.customLang;
  const statusDoneReport = report[0]?.status === "done" || report[0]?.status === "done-delayed";

  const [levelId, setLevelId] = useState(1);
  const [contentCycle, setContentCycle] = useState(1);

  const handleChangeLevel = (value) => {
    const newLevel = { 1: "A0", 2: "A1", 3: "A2", 4: "B1", 5: "B2", 6: "C1", 7: "C2" };

    setLevelId(value);
    getClycle(newLevel[value]);
  };

  const handleChangeCycle = (value) => setContentCycle(value);

  const handleClose = () => setShowModal(false);

  const getClycle = async (level) => {
    await dispatch(cycleByLevelAndLanguage(level, report[0]?.lingoLanguageId));
  };

  const getNextContent = async () => {
    const payload = {
      limit: 10,
      studentId: report[0]?.studentId,
      change: statusDoneReport ? true : false,
      lingoLanguageId: report[0]?.lingoLanguageId,
      sequentialScheduleId: report[0]?.sequentialScheduleId,
    };

    await dispatch(getNextContentsOfStudent(payload));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const payload = [
      {
        levelId,
        contentCycle,
        oralGrade: 0,
        writingGrade: 0,
        averageGrade: 0,
        dictationGrade: 0,
        studentId: report[0]?.studentId,
        lingoLanguageId: report[0]?.lingoLanguageId,
      },
    ];

    if (currentCycleAndLevelStudent.length === 0) {
      await dispatch(createStudentLevelGrade(payload)).then(async (resp) => {
        if (resp) {
          const payload = { studentId: report[0]?.studentId, language: report[0]?.lingoLanguageId };
          await dispatch(getCurrentCycleAndLevelByStudentId(payload));

          getNextContent();
          handleClose();
        }
      });
    } else {
      const newPayload = { ...payload[0], id: currentCycleAndLevelStudent[0].id };

      await dispatch(updateStudentLevelGrade(newPayload)).then(async (resp) => {
        if (resp) {
          const payload = { studentId: report[0]?.studentId, language: report[0]?.lingoLanguageId };
          await dispatch(getCurrentCycleAndLevelByStudentId(payload));

          getNextContent();
          handleClose();
        }
      });
    }
  };

  useEffect(() => {
    if (currentCycleAndLevelStudent.length === 0) {
      getClycle("A0");
    } else {
      const { level, levelId, contentCycle } = currentCycleAndLevelStudent[0];

      getClycle(level);
      setLevelId(levelId);
      setContentCycle(contentCycle);
    }

    // eslint-disable-next-line
  }, [currentCycleAndLevelStudent]);

  return (
    <Dialog
      open={showModal}
      onClose={handleClose}
      className="modalPrivilege"
      classes={"modalSelectLevelStudent"}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <div className="containerSelectLevelStudent">
        <h3 className="title">{t("STUDENT_LEVEL")}</h3>

        <div className="levels">
          {maxLevelId >= 1 && (
            <div className="level">
              <label>{t("BASIC")}</label>

              <div className="rowLevels">
                {maxLevelId >= 1 && (
                  <div
                    onClick={() => handleChangeLevel(1)}
                    className={`rowLevelsCircle green_100 ${levelId === 1 && "levelActice"}`}
                  >
                    A0
                  </div>
                )}
                {maxLevelId >= 2 && (
                  <div
                    onClick={() => handleChangeLevel(2)}
                    className={`rowLevelsCircle green_700 ${levelId === 2 && "levelActice"}`}
                  >
                    A1
                  </div>
                )}
                {maxLevelId >= 3 && (
                  <div
                    onClick={() => handleChangeLevel(3)}
                    className={`rowLevelsCircle green_400 ${levelId === 3 && "levelActice"}`}
                  >
                    A2
                  </div>
                )}
              </div>
            </div>
          )}

          {maxLevelId >= 4 && (
            <div className="level">
              <label>{t(customLang ? "INTERVIEW" : "INTERMEDIATE")}</label>
              <div className="rowLevels">
                {maxLevelId >= 4 && (
                  <div
                    onClick={() => handleChangeLevel(4)}
                    className={`rowLevelsCircle yellow_100 ${levelId === 4 && "levelActice"}`}
                  >
                    B1
                  </div>
                )}
                {maxLevelId >= 5 && (
                  <div
                    onClick={() => handleChangeLevel(5)}
                    className={`rowLevelsCircle yellow_700 ${levelId === 5 && "levelActice"}`}
                  >
                    B2
                  </div>
                )}
              </div>
            </div>
          )}

          {maxLevelId >= 6 && (
            <div className="level">
              <label>{t("LINGO_ADV")}</label>
              <div className="rowLevels">
                {maxLevelId >= 6 && (
                  <div
                    onClick={() => handleChangeLevel(6)}
                    className={`rowLevelsCircle red_100 ${levelId === 6 && "levelActice"}`}
                  >
                    C1
                  </div>
                )}
                {maxLevelId >= 7 && (
                  <div
                    onClick={() => handleChangeLevel(7)}
                    className={`rowLevelsCircle red_500 ${levelId === 7 && "levelActice"}`}
                  >
                    C2
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="cycles">
          <p>{t("CYCLE")}</p>

          <div className="cycle">
            {cycles?.map((cycle, index) => (
              <div
                key={index}
                onClick={() => handleChangeCycle(index + 1)}
                className={`cycleCircle ${contentCycle === index + 1 && "cycleActice"} ${handleSelectColor(
                  cycle?.level,
                )}`}
              >
                {cycle?.cycle}
              </div>
            ))}
          </div>
        </div>

        <div className="footer">
          <button type="submit" onClick={handleSave} className="buttonModalSave">
            {t("ACCEPT")}
          </button>
          <button type="button" onClick={handleClose} className="buttonModalBack">
            {t("BACK")}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

export default ModalSelectLevelStudent;
